import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DAY_MS, fetchSummary, isoDay, Loaded, londonToday, parseDay } from './api';
import type { Check } from './SystemsCard';

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const POLL_MS = 60_000;
const KEEP_CHECKS = 20;

export type Range = { from: string; to: string };

/** Range from the URL (?from=&to=), else the last 30 days. */
const readRange = (params: URLSearchParams, today: string): Range => {
  const from = params.get('from');
  const to = params.get('to');
  if (from && to && DAY_RE.test(from) && DAY_RE.test(to) && from <= to && to <= today) return { from, to };
  return { from: isoDay(parseDay(today) - 29 * DAY_MS), to: today };
};

/**
 * The admin summary (api/functions/admin-dashboard.js /summary) for the range in the URL,
 * refreshed every minute while the tab is visible. Shared by the Dashboard and Analytics pages;
 * each keeps its own range in its URL (the nav links open the default 30 days).
 */
export function useSummary() {
  const [params, setParams] = useSearchParams();
  const today = londonToday();
  const range = readRange(params, today);
  const days = Math.round((parseDay(range.to) - parseDay(range.from)) / DAY_MS) + 1;

  const [data, setData] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Chat assistant health, one point per refresh, for the systems tile's track.
  const [checks, setChecks] = useState<Check[]>([]);
  const ctrl = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    ctrl.current?.abort();
    const ac = new AbortController();
    ctrl.current = ac;
    setLoading(true);
    const at = Date.now();
    try {
      const d = await fetchSummary(range.from, range.to, ac.signal);
      setData(d);
      setError(null);
      const h = d.health;
      if (!('error' in h)) setChecks((c) => [...c, { at, ok: h.chat.ok, ms: h.chat.ms }].slice(-KEEP_CHECKS));
    } catch (e) {
      if (ac.signal.aborted) return;
      setError(e instanceof Error ? e.message : 'Could not reach the API');
    } finally {
      if (!ac.signal.aborted) setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => {
      window.clearInterval(id);
      ctrl.current?.abort();
    };
  }, [load]);

  return {
    range,
    days,
    today,
    setRange: (r: Range) => setParams({ from: r.from, to: r.to }),
    data,
    error,
    loading,
    checks,
    reload: load,
    // Data for another range stays on screen, dimmed, while the new one loads.
    stale: !!data && (data.range.from !== range.from || data.range.to !== range.to),
  };
}

export type SummaryState = ReturnType<typeof useSummary>;
